import React, { useState, useRef, useEffect, useMemo } from 'react';

import { TreeTable, TreeState, Row } from 'cp-react-tree-table';

import './CategoriesTable.scss';
import { addModifiers, getLanguageCode } from '../../../shared/helpers';
import { prepareCategoriesForTable } from './helpers';
import { DataCategory } from '../../../shared/api/api';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { TableDataCategory } from './types';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { FiltersControl } from '../Filters/types';
import { useWatch } from 'react-hook-form';
import { filterFields } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import { useWindowSize } from 'react-use';
import { MOBILE_BREAKPOINTS } from '../../../shared/constants';
import { CopyToClipboard } from '../CopyToClipboard/CopyToClipboard';

interface Props {
  onCategoryClick?: (category: TableDataCategory) => void;
  categories: DataCategory[];
  selectedCategory?: DataCategory;
  control: FiltersControl;
}

export const CategoriesTable = ({ onCategoryClick: parentOnCategoryClick, categories, selectedCategory, control }: Props) => {
  const rowNodes = useMemo(() => prepareCategoriesForTable(categories), [categories]);
  const [treeState, setTreeState] = useState<TreeState<TableDataCategory>>(() => TreeState.create<TableDataCategory>(rowNodes));
  const selectedRowModel = useMemo(() => {
    return treeState.data.find(rowModel => rowModel.data.id === selectedCategory?.id);
  }, [selectedCategory]);
  const regionValue = useWatch({ control, name: filterFields.region.name });

  const trackEvent = useDataExplorerTrackCustomEvent();

  useEffect(() => {
    if (selectedRowModel) {
      let parent = selectedRowModel?.data._parent;

      // open parents of selected category
      while (parent) {
        // eslint-disable-next-line  no-loop-func
        const parentRowModel = treeState.data.find(rowModel => rowModel.data?.id === parent?.id);

        if (parentRowModel) {
          setTreeState(TreeState.toggleChildren(treeState, parentRowModel));
        }

        parent = parentRowModel?.data._parent;
      }

      // applly active styles to selected category
      const selector = `.cp_tree-table_viewport [data-index="${selectedRowModel?.metadata.index}"]`;
      const styleEl = document.createElement('style');
      document.head.appendChild(styleEl);
      const styleSheet = styleEl.sheet;

      styleSheet?.insertRule(`${selector} {background: #2991d1 !important; color: #ffffff !important;}`, 0);

      return () => {
        styleEl?.remove();
      };
    }
  }, [selectedRowModel]);

  const treeTableRef = useRef<TreeTable<TableDataCategory>>(null);

  const onCategoryClick = (category: TableDataCategory) => {
    parentOnCategoryClick?.(category);
  };
  const renderHeaderCell = (id: string) => {
    return () => {
      return (
        <span className="categories-table__header-cell">
          <FormattedMessageHelper id={id} />
        </span>
      );
    };
  };

  const renderIndexCell = (row: Row<TableDataCategory>) => {
    const {
      $state: { isExpanded },
      data,
    } = row;
    const { hasChildren, depth } = row.metadata;

    const cellModifiers = (() => {
      let result = [];
      if (isExpanded) {
        result.push('expanded');
      }
      if (!hasChildren) {
        result.push('no-children');
      }
      return result;
    })();

    return (
      <div className={addModifiers('categories-table__index-cell', cellModifiers)} style={{ paddingLeft: depth * 15 + 'px' }}>
        <button
          data-testid={`data-categories-toggle-row`}
          className="categories-table__toggle-button"
          onClick={() => {
            row.toggleChildren();
            trackEvent('de_click_category_expand');
          }}
          disabled={!hasChildren}
        ></button>
        <div>
          <button onClick={() => onCategoryClick(data)} className="categories-table__name-button">
            {data.name}
          </button>
          <p className="categories-table__category_id">
            <span>{data.id}</span>
            <CopyToClipboard className="copy-to-clipboard--category-id" value={data.id} />
          </p>
        </div>
      </div>
    );
  };

  const renderIntegerCell = (data: number) => {
    return <span className="categories-table__cell">{data.toLocaleString(getLanguageCode())}</span>;
  };
  const renderFloatCell = (data: number) => {
    return <span className="categories-table__cell">{parseFloat(data.toFixed(2)).toLocaleString(getLanguageCode())}</span>;
  };

  const renderRegionsCell = (regions: string[]) => {
    let activeDisplayRegion: string | null = null;
    const restRegions = [];

    for (const region of regions) {
      if (region === regionValue) {
        activeDisplayRegion = region;
      } else {
        restRegions.push(region);
      }
    }

    const regionsToRender = [
      activeDisplayRegion && (
        <span key={0} style={{ fontWeight: 'bold' }}>
          {activeDisplayRegion}
        </span>
      ),
      ...restRegions,
    ].filter(Boolean);

    return (
      <span className="categories-table__cell">
        <div className="categories-table__regions">
          {regionsToRender.map((item, i) => {
            return (
              <React.Fragment key={String(i)}>
                {item}
                {regionsToRender[i + 1] && ', '}
              </React.Fragment>
            );
          })}
        </div>
      </span>
    );
  };

  const { width, height } = useWindowSize();

  const MODAL_HEADER_AND_PADDINGS_HEIGHT = 180;
  const modalHeight = width > MOBILE_BREAKPOINTS.table.width ? 0.9 * height : height;
  const tableHeight = modalHeight - MODAL_HEADER_AND_PADDINGS_HEIGHT;

  return (
    <div className={'categories-table__wrapper'} data-testid={'categories-table'}>
      <TreeTable<TableDataCategory>
        className="categories-table"
        height={tableHeight}
        headerHeight={46}
        ref={treeTableRef}
        value={treeState}
        onChange={(newVal: TreeState<TableDataCategory>) => {
          setTreeState(newVal);
        }}
      >
        <TreeTable.Column
          renderCell={renderIndexCell}
          renderHeaderCell={renderHeaderCell('dataExplorer.filters.categoriesLabel')}
          basis="200px"
          grow={1}
        />

        <TreeTable.Column<TableDataCategory>
          renderCell={row => renderRegionsCell(row.data.region)}
          renderHeaderCell={renderHeaderCell('dataExplorer.filters.regionsLabel')}
          basis="235px"
          grow={1}
        />
        <TreeTable.Column<TableDataCategory>
          renderCell={row => renderIntegerCell(row.data.datasetCount)}
          renderHeaderCell={renderHeaderCell('dataExplorer.filters.datasetsLabel')}
        />
        <TreeTable.Column<TableDataCategory>
          renderCell={row => renderIntegerCell(row.data.fieldCount)}
          renderHeaderCell={renderHeaderCell('dataExplorer.filters.fieldsLabel')}
        />
        <TreeTable.Column<TableDataCategory>
          renderCell={row => renderIntegerCell(row.data.alphaCount)}
          renderHeaderCell={renderHeaderCell('dataExplorer.filters.alphasLabel')}
          basis={'50px'}
        />
        <TreeTable.Column<TableDataCategory>
          renderCell={row => renderFloatCell(row.data.valueScore)}
          renderHeaderCell={renderHeaderCell('dataExplorer.filters.valueScoreLabel')}
        />
      </TreeTable>
    </div>
  );
};
